/**
 * useDarkMode — global, persisted dark-mode preference (redesign item 1).
 * Tiny external store so AppNav + pages stay in sync without a provider.
 * Pages that support the dark theme (Feed) apply it; the preference is
 * remembered across sessions via localStorage.
 */
import { useSyncExternalStore, useCallback } from "react";

const KEY = "ti_dark_mode";
const listeners = new Set();
let current = (() => {
  try { return localStorage.getItem(KEY) === "1"; } catch { return false; }
})();

// Site-wide theme application: every page gets html.dark + dark body bg.
function applyTheme() {
  try {
    document.documentElement.classList.toggle("dark", current);
    document.documentElement.style.backgroundColor = current ? "#0c1011" : "";
    document.body.style.backgroundColor = current ? "#0c1011" : "";
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", current ? "#0c1011" : "#006874");
  } catch { /* SSR/tests */ }
}
applyTheme();

function subscribe(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function useDarkMode() {
  const value = useSyncExternalStore(subscribe, () => current);
  const set = useCallback((v) => {
    current = typeof v === "function" ? v(current) : v;
    try { localStorage.setItem(KEY, current ? "1" : "0"); } catch {}
    applyTheme();
    listeners.forEach((cb) => cb());
  }, []);
  return [value, set];
}
